import type { WebViewResponse, IncomingEvent, IncomingMessage, IncomingTypedEvent } from './types/messages';
import { sendToTerminal, sendToTestLog } from '../terminalLogger';
import { LogCategory } from '@/config/logging';
import type { EventManager } from './communication/events/eventManager';

/**
 * MessageRouter - ÚNICO ponto de entrada para mensagens do backend C#
 * 
 * Converte qualquer evento recebido do WebView2 em um tipo coerente
 * e expõe uma função para registrar o listener global único.
 * 
 * Suporta dois tipos de mensagens:
 * 1. WebViewResponse: Respostas a comandos (tem requestId + status)
 * 2. IncomingEvent: Eventos espontâneos do C# (tem apenas action, sem requestId)
 * 
 * Eventos são despachados via EventManager para centralização.
 * 
 * RESPONSABILIDADE EXCLUSIVA:
 * - Processa TODOS os shortcuts (type: "shortcut") do backend
 * - Emite evento customizado "shortcut:received" para o ToolsMenu.tsx
 * - NENHUM outro componente deve processar mensagens de shortcut diretamente
 * 
 * @see docs/SHORTCUTS_ARCHITECTURE.md para detalhes do fluxo completo
 */
export class MessageRouter {
  private static readonly KNOWN_TYPED_EVENTS = new Set<string>([
    'tools:session-token',
    'tools:session-bootstrap',
    'shortcut',
    'navigate-to-tool',
    'navigate-to-url',
    'tools:bootstrap-session:ack',
    'docker:active'
  ]);

  private isListenerConfigured = false;
  private defaultCallback: ((response: WebViewResponse) => void) | null = null;
  private eventManager: EventManager | null = null;

  constructor(eventManager?: EventManager) {
    this.eventManager = eventManager || null;
  }

  private buildDataPreview(data: any): string {
    try {
      if (typeof data === 'string') {
        return data.substring(0, 400);
      }

      return JSON.stringify(data).substring(0, 400);
    } catch {
      return String(data);
    }
  }

  /**
   * Processa dados recebidos e determina se é resposta ou evento
   */
  processResponseData(data: any): IncomingMessage {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    
    if (typeof parsed !== 'object' || parsed === null) {
      throw new Error('Invalid message format: must be an object');
    }

    if ('requestId' in parsed && 'status' in parsed) {
      return parsed as WebViewResponse;
    }

    if ('action' in parsed && !('requestId' in parsed)) {
      return parsed as IncomingEvent;
    }

    if (
      'type' in parsed &&
      typeof (parsed as any).type === 'string' &&
      !('requestId' in parsed) &&
      MessageRouter.KNOWN_TYPED_EVENTS.has((parsed as any).type)
    ) {
      return parsed as IncomingTypedEvent;
    }

    throw new Error('Message must have either (requestId + status), (action without requestId), or a known type event');
  }


  setupListenerInternal(onResponse: (response: WebViewResponse, raw?: any) => void): boolean {
    if (this.isListenerConfigured) return true;
    
    const handleWebViewMessage = (event: MessageEvent) => {
      // ✅ Log reduzido - apenas para debug quando necessário
      // Removido log automático de todas as mensagens para reduzir poluição
      
      try {
        const message = this.processResponseData(event.data);
        
        // ✅ Log após parsing bem-sucedido
        const isResponse = 'requestId' in message && 'status' in message;
        const isEvent = 'action' in message && !('requestId' in message);
        
        // 🔇 Logs de parsing removidos para reduzir verbosidade
        // (cada mensagem gerava múltiplas linhas de log)
        
        // Se é RESPOSTA → processar como response
        if (isResponse) {
          onResponse(message as WebViewResponse, event.data);
          if (this.defaultCallback) {
            try { this.defaultCallback(message as WebViewResponse); } catch {}
          }
        }
        // ✅ SHORTCUT: Mensagens de atalho do backend (type: "shortcut")
        // 
        // ARQUITETURA:
        // Este é o ÚNICO ponto que processa shortcuts do backend.
        // Fluxo: Backend C# → MessageRouter → shortcut:received → ToolsMenu.tsx
        // 
        // IMPORTANTE: useBootstrapListener NÃO processa shortcuts (delegado para cá)
        // 
        else if ((message as any).type === 'shortcut') {
          const shortcutData = message as any;
          sendToTerminal('⌨️ [MESSAGE_ROUTER] Shortcut recebido do backend', {
            type: shortcutData.type,
            action: shortcutData.action,
            combo: shortcutData.combo,
            ts: shortcutData.ts,
            _debug_publishId: shortcutData._debug_publishId // Para rastreamento de duplicatas
          }, LogCategory.EVENT);
          
          // Emitir evento customizado que o ToolsMenu.tsx escuta
          try {
            window.dispatchEvent(new CustomEvent('shortcut:received', {
              detail: {
                action: shortcutData.action,
                combo: shortcutData.combo,
                ts: shortcutData.ts,
                _debug_publishId: shortcutData._debug_publishId
              }
            }));
            sendToTerminal('✅ [MESSAGE_ROUTER] Evento shortcut:received emitido', {
              action: shortcutData.action,
              combo: shortcutData.combo,
              _debug_publishId: shortcutData._debug_publishId
            }, LogCategory.EVENT);
          } catch (err) {
            sendToTerminal('❌ [MESSAGE_ROUTER] Erro ao emitir evento shortcut:received', {
              error: err instanceof Error ? err.message : String(err)
            }, LogCategory.ERROR);
          }
        }
        // ✅ BOOTSTRAP: Mensagens de bootstrap/sessão (tools:session-token)
        else if ((message as any).type === 'tools:session-token') {
          const tokenData = message as any;
          const bootstrapDataForBuffer = message as any;
          try {
            (window as any).__ONEPRINT_LAST_TOOLS_BOOTSTRAP__ = {
              dockerId: bootstrapDataForBuffer.dockerId,
              sessionEpoch: bootstrapDataForBuffer.sessionEpoch,
              tokenVersion: bootstrapDataForBuffer.tokenVersion,
              sessionToken: bootstrapDataForBuffer.sessionToken,
              hmacKeyB64: bootstrapDataForBuffer.hmacKeyB64,
              frontendUrl: bootstrapDataForBuffer.frontendUrl,
              issuedAt: bootstrapDataForBuffer.issuedAt,
              source: 'webview',
              capturedAt: Date.now()
            };
          } catch {}
          sendToTerminal('🔐 [MESSAGE_ROUTER] Token de sessão recebido', {
            type: tokenData.type,
            hasToken: !!tokenData.sessionToken,
            hasHmacKey: !!tokenData.hmacKeyB64,
            dockerId: tokenData.dockerId,
            sessionEpoch: tokenData.sessionEpoch,
            tokenVersion: tokenData.tokenVersion
          }, LogCategory.AUTH);
          
          try {
            window.dispatchEvent(new CustomEvent('session:token-received', {
              detail: {
                sessionToken: tokenData.sessionToken,
                hmacKeyB64: tokenData.hmacKeyB64,
                source: 'webview',
                dockerId: tokenData.dockerId,
                sessionEpoch: tokenData.sessionEpoch,
                tokenVersion: tokenData.tokenVersion
              }
            }));
          } catch (err) {
            sendToTerminal('❌ [MESSAGE_ROUTER] Erro ao emitir evento session:token-received', {
              error: err instanceof Error ? err.message : String(err)
            }, LogCategory.ERROR);
          }
        }
        else if ((message as any).type === 'tools:session-bootstrap') {
          const bootstrapDataForBuffer = message as any;
          try {
            (window as any).__ONEPRINT_LAST_TOOLS_BOOTSTRAP__ = {
              dockerId: bootstrapDataForBuffer.dockerId,
              sessionEpoch: bootstrapDataForBuffer.sessionEpoch,
              tokenVersion: bootstrapDataForBuffer.tokenVersion,
              sessionToken: bootstrapDataForBuffer.sessionToken,
              hmacKeyB64: bootstrapDataForBuffer.hmacKeyB64,
              frontendUrl: bootstrapDataForBuffer.frontendUrl,
              issuedAt: bootstrapDataForBuffer.issuedAt,
              source: 'webview',
              capturedAt: Date.now()
            };
          } catch {}
          const tokenData = message as any;
          sendToTerminal('🚀 [MESSAGE_ROUTER] Bootstrap de sessão do tools recebido', {
            dockerId: tokenData.dockerId,
            sessionEpoch: tokenData.sessionEpoch,
            tokenVersion: tokenData.tokenVersion,
            hasToken: !!tokenData.sessionToken,
            hasHmacKey: !!tokenData.hmacKeyB64,
            rawPreview: this.buildDataPreview(tokenData)
          }, LogCategory.AUTH);

          try {
            window.dispatchEvent(new CustomEvent('tools:session-bootstrap-received', {
              detail: {
                dockerId: tokenData.dockerId,
                sessionEpoch: tokenData.sessionEpoch,
                tokenVersion: tokenData.tokenVersion,
                sessionToken: tokenData.sessionToken,
                hmacKeyB64: tokenData.hmacKeyB64,
                frontendUrl: tokenData.frontendUrl,
                issuedAt: tokenData.issuedAt,
                source: 'webview'
              }
            }));
            sendToTerminal('✅ [MESSAGE_ROUTER] Evento tools:session-bootstrap-received emitido', {
              dockerId: tokenData.dockerId,
              sessionEpoch: tokenData.sessionEpoch,
              tokenVersion: tokenData.tokenVersion
            }, LogCategory.AUTH);
          } catch (err) {
            sendToTerminal('❌ [MESSAGE_ROUTER] Erro ao emitir evento tools:session-bootstrap-received', {
              error: err instanceof Error ? err.message : String(err)
            }, LogCategory.ERROR);
          }
        }
        // ✅ BOOTSTRAP: Dados de bootstrap da sessão
        else if ((message as any).action === 'bootstrap-session' || (message as any).bootstrap) {
          const bootstrapData = message as any;
          sendToTerminal('🚀 [MESSAGE_ROUTER] Bootstrap recebido', {
            action: bootstrapData.action,
            hasData: !!bootstrapData.data,
            hasBootstrap: !!bootstrapData.bootstrap
          }, LogCategory.CONNECTION);
          
          try {
            window.dispatchEvent(new CustomEvent('bootstrap:received', {
              detail: {
                data: bootstrapData.data || bootstrapData.bootstrap,
                rawData: bootstrapData
              }
            }));
          } catch (err) {
            sendToTerminal('❌ [MESSAGE_ROUTER] Erro ao emitir evento bootstrap:received', {
              error: err instanceof Error ? err.message : String(err)
            }, LogCategory.ERROR);
          }
        }
        // ✅ NAVIGATE: Navegação para ferramenta específica
        else if ((message as any).type === 'navigate-to-tool') {
          const navData = message as any;
          sendToTerminal('🧭 [MESSAGE_ROUTER] Navegação para ferramenta', {
            type: navData.type,
            toolId: navData.toolId
          }, LogCategory.EVENT);
          
          try {
            window.dispatchEvent(new CustomEvent('navigate:to-tool', {
              detail: {
                toolId: navData.toolId
              }
            }));
          } catch (err) {
            sendToTerminal('❌ [MESSAGE_ROUTER] Erro ao emitir evento navigate:to-tool', {
              error: err instanceof Error ? err.message : String(err)
            }, LogCategory.ERROR);
          }
        }
        // ✅ NAVIGATE: Navegação para URL
        else if ((message as any).type === 'navigate-to-url') {
          const navData = message as any;
          sendToTerminal('🧭 [MESSAGE_ROUTER] Navegação para URL', {
            type: navData.type,
            url: navData.url
          }, LogCategory.EVENT);
          
          try {
            window.dispatchEvent(new CustomEvent('navigate:to-url', {
              detail: {
                url: navData.url
              }
            }));
          } catch (err) {
            sendToTerminal('❌ [MESSAGE_ROUTER] Erro ao emitir evento navigate:to-url', {
              error: err instanceof Error ? err.message : String(err)
            }, LogCategory.ERROR);
          }
        }
        // ✅ BOOTSTRAP ACK: Confirmação de bootstrap com snapshot
        else if ((message as any).type === 'tools:bootstrap-session:ack') {
          const ackData = message as any;
          sendToTerminal('✅ [MESSAGE_ROUTER] Bootstrap ACK recebido', {
            type: ackData.type,
            hasSnapshot: !!ackData.snapshot
          }, LogCategory.CONNECTION);
          
          try {
            window.dispatchEvent(new CustomEvent('bootstrap:ack-received', {
              detail: {
                snapshot: ackData.snapshot
              }
            }));
          } catch (err) {
            sendToTerminal('❌ [MESSAGE_ROUTER] Erro ao emitir evento bootstrap:ack-received', {
              error: err instanceof Error ? err.message : String(err)
            }, LogCategory.ERROR);
          }
        }
        // ✅ DOCKER ACTIVE: Mensagem de status do Docker
        else if ((message as any).type === 'docker:active') {
          // Ignorar silenciosamente (mensagem de heartbeat)
        }
        // Se é EVENTO → despachar via EventManager
        else if (isEvent) {
          const incomingEvent = message as IncomingEvent;
          
          if (this.eventManager) {
            try {
              // Unwrap automático de result.data para notificações monitorar-objetos
              // Backend envia: { action, status, result: { action, data: {...} } }
              let dataToDispatch = incomingEvent.data ?? incomingEvent;
              
              // Se notificação tem estrutura result.data (monitorar-objetos e outras)
              if (incomingEvent.result && typeof incomingEvent.result === 'object') {
                if ('data' in incomingEvent.result) {
                  dataToDispatch = incomingEvent.result.data;
                  // 🔇 Log de unwrap removido para reduzir verbosidade
                }
              }
              
              // Despachar para EventManager (que chama handlers registrados)
              this.eventManager.dispatch(incomingEvent.action, dataToDispatch);
            } catch (error) {
              sendToTerminal('❌ [MESSAGE_ROUTER] Erro ao despachar evento', {
                action: incomingEvent.action,
                error: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : undefined
              }, LogCategory.ERROR);
            }
          } else {
            sendToTerminal('⚠️ [MESSAGE_ROUTER] EventManager não configurado', {
              action: incomingEvent.action
            }, LogCategory.ERROR);
          }
        }
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        const errorStack = e instanceof Error ? e.stack : undefined;
        const errorName = e instanceof Error ? e.name : undefined;
        let parsedPreview: string | null = null;
        let rawStringLength: number | null = null;

        try {
          if (typeof event.data === 'string') {
            rawStringLength = event.data.length;
            parsedPreview = this.buildDataPreview(JSON.parse(event.data));
          } else {
            parsedPreview = this.buildDataPreview(event.data);
          }
        } catch (parseError) {
          parsedPreview = `[parse-failed] ${parseError instanceof Error ? parseError.message : String(parseError)}`;
        }
        
        sendToTerminal('❌ [MESSAGE_ROUTER] Erro ao processar mensagem do WebView2', {
          errorName,
          error: errorMessage,
          stack: errorStack,
          dataType: typeof event.data,
          rawStringLength,
          parsedPreview,
          dataPreview: typeof event.data === 'string' 
            ? event.data.substring(0, 200) 
            : (typeof event.data === 'object' ? JSON.stringify(event.data).substring(0, 200) : String(event.data))
        }, LogCategory.ERROR);
      }
    };
    
    if ((window as any)?.chrome?.webview) {
      (window as any).chrome.webview.addEventListener('message', handleWebViewMessage);
      this.isListenerConfigured = true;
      sendToTerminal('✅ [MESSAGE_ROUTER] Listener do WebView2 configurado', {}, LogCategory.CONNECTION);
    } else {
      sendToTerminal('❌ [MESSAGE_ROUTER] WebView2 não disponível - listener não configurado', {}, LogCategory.ERROR);
    }
  }

  setupListener(callback: (response: WebViewResponse) => void): () => void {
    this.defaultCallback = callback;
    return () => { this.defaultCallback = null; };
  }
}


